import { useCallback, useMemo } from 'react'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Maybe } from 'types/graphqlTypes'
import { useSendPasswordLink } from 'apps/user/apiTypes/password'
import { userTypeBooleans } from 'utils/userTypes'
import { IUserGroupBooleans, USER_TYPE } from 'constants/userTypes'
import { IApplication, TBasePasswordLinkTypes, TPasswordLinkTypes } from '../types'

const PASSWORD_LINK_TYPE_FOR_APP: Record<
  TBasePasswordLinkTypes,
  Record<IApplication, TPasswordLinkTypes>
> = {
  login_link: {
    [IApplication.Marketplace]: 'login_link',
    [IApplication.CarrierApp]: 'fleet_planner_login_link',
    [IApplication.ShipperApp]: 'tms_login_link',
  },
  reset_password: {
    [IApplication.Marketplace]: 'reset_password',
    [IApplication.CarrierApp]: 'fleet_planner_reset_password_link',
    [IApplication.ShipperApp]: 'tms_reset_password_link',
  },
}

const MESSAGE_LOGIN_LINK_SUCCESS =
  'Login link was sent to the user, ask them to check their email inbox.'
const MESSAGE_LOGIN_LINK_FAILURE = 'There was an error sending login link.'
const MESSAGE_RESET_PASSWORD_LINK_SUCCESS =
  'Password reset link was sent to the user, ask them to check their email inbox.'
const MESSAGE_RESET_PASSWORD_LINK_FAILURE = 'There was an error sending reset password link.'

export interface IUseUserProfileHeaderActionsProps {
  email: Maybe<string>
  userType: USER_TYPE
}

export const useUserProfileHeaderActions = ({
  email,
  userType,
}: IUseUserProfileHeaderActionsProps) => {
  const { openToast } = useGlobalToast()
  const { loading, mutate: sendPasswordLinkMutation } = useSendPasswordLink({})

  const app = useMemo(() => {
    const userTypeBools = userTypeBooleans(userType) as IUserGroupBooleans
    if (userTypeBools.isDispatcher || userTypeBools.isOwnerOperator) {
      return IApplication.CarrierApp
    }
    if (userTypeBools.isShipper) {
      return IApplication.ShipperApp
    }
    return IApplication.Marketplace
  }, [userType])

  const sendPasswordLink = useCallback(
    async (baseType: TBasePasswordLinkTypes) => {
      if (!email) {
        return
      }

      try {
        const type = PASSWORD_LINK_TYPE_FOR_APP[baseType][app]
        await sendPasswordLinkMutation({
          email,
          type,
        })

        openToast({
          toastMessage:
            baseType === 'login_link'
              ? MESSAGE_LOGIN_LINK_SUCCESS
              : MESSAGE_RESET_PASSWORD_LINK_SUCCESS,
        })
      } catch {
        openToast({
          toastMessage:
            baseType === 'login_link'
              ? MESSAGE_LOGIN_LINK_FAILURE
              : MESSAGE_RESET_PASSWORD_LINK_FAILURE,
          toastType: 'alert',
        })
      }
    },
    [app, email, openToast, sendPasswordLinkMutation]
  )

  return {
    loading,
    sendPasswordLink,
  }
}
